import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type ReportVariant = "screen" | "pdf";

/** Same label style HorizonComparisonGrid uses for a horizon column. */
export function HorizonLabel({ children }: { children: ReactNode }) {
  return <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-400">{children}</p>;
}

export function SectionHeading({ children }: { children: ReactNode }) {
  return <h2 className="text-lg font-semibold">{children}</h2>;
}

interface ReportPageProps {
  variant: ReportVariant;
  companyName: string;
  /** No page break after the last logical page. */
  last?: boolean;
  /** Content owns its own padding and background (the dark cover), so the
   * screen card drops its padding and footer. The PDF variant is unaffected:
   * it never had padding of its own. */
  bleed?: boolean;
  children: ReactNode;
}

/**
 * One logical page. Screen: an A4-proportioned paper card (210 mm wide, 14 mm
 * padding = the PDF's 182 mm content width). PDF: bare content — Chromium
 * owns margins and the footer, so a fixed-height card would fight pagination.
 * Preview page breaks are logical only; Chromium decides the real ones.
 *
 * The PDF variant is deliberately NOT flex: Chromium fragments flex containers
 * unreliably and widely ignores `break-inside: avoid` on flex items, which is
 * this report's entire pagination strategy. Block layout with margin spacing
 * paginates predictably. Flex is kept for the screen paper cards, which never
 * paginate.
 */
export function ReportPage({ variant, companyName, last = false, bleed = false, children }: ReportPageProps) {
  if (variant === "pdf") {
    return <section className={cn("space-y-6", !last && "break-after-page")}>{children}</section>;
  }

  if (bleed) {
    return (
      <section
        data-report-page
        className="mx-auto min-h-[297mm] w-[210mm] overflow-hidden rounded-lg border border-border bg-white shadow-sm"
      >
        {children}
      </section>
    );
  }

  return (
    <section
      data-report-page
      className="mx-auto flex min-h-[297mm] w-[210mm] flex-col justify-between rounded-lg border border-border bg-white p-[14mm] shadow-sm"
    >
      <div className="flex flex-col gap-6">{children}</div>
      <p className="mt-8 border-t border-border pt-2 text-center text-[10px] text-muted-foreground">
        {companyName} · 180Pi climate risk framework
      </p>
    </section>
  );
}
